import React, { useState, useEffect, useCallback } from "react";
import {
  X, Wallet, RefreshCw, Plus, Minus, Lock, Unlock,
  History, AlertTriangle, TrendingDown, TrendingUp,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { formatIndianCurrency } from "../utils/Const";

const fmt = (n) => `₹${formatIndianCurrency(n || 0)}`;

export default function CashDrawerPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [floatInput, setFloatInput] = useState("");
  const [entry, setEntry] = useState({ type: "OUT", amount: "", reason: "" });
  const [actualInput, setActualInput] = useState("");
  const [closing, setClosing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/cash-drawer/today");
      setData(res.data);
    } catch {
      toast.error("Failed to load cash drawer");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const open = async () => {
    if (floatInput === "" || Number(floatInput) < 0) { toast.error("Enter opening float"); return; }
    setBusy(true);
    try {
      await api.post("/cash-drawer/open", { openingFloat: Number(floatInput) });
      toast.success("Drawer opened");
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to open drawer");
    } finally {
      setBusy(false);
    }
  };

  const addEntry = async () => {
    if (!entry.amount || Number(entry.amount) <= 0 || !entry.reason.trim()) {
      toast.error("Amount and reason are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/cash-drawer/entry", { ...entry, amount: Number(entry.amount) });
      setEntry({ type: "OUT", amount: "", reason: "" });
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add entry");
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (actualInput === "" || Number(actualInput) < 0) { toast.error("Count the cash and enter the actual amount"); return; }
    setBusy(true);
    try {
      const res = await api.post("/cash-drawer/close", { actualClosing: Number(actualInput) });
      const v = res.data.variance;
      if (v === 0) toast.success("Drawer closed — cash tallies perfectly ✓");
      else if (v > 0) toast.warn(`Drawer closed — ₹${v} excess`);
      else toast.error(`Drawer closed — ₹${Math.abs(v)} SHORT`);
      setClosing(false);
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to close drawer");
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get("/cash-drawer/history");
      setHistory(res.data);
    } catch { /* manager-only; ignore for cashier */ }
  };

  const toggleHistory = () => {
    if (!showHistory) loadHistory();
    setShowHistory(v => !v);
  };

  const drawer = data?.drawer;
  const inSum = drawer?.entries?.filter(e => e.type === "IN").reduce((s, e) => s + e.amount, 0) || 0;
  const outSum = drawer?.entries?.filter(e => e.type === "OUT").reduce((s, e) => s + e.amount, 0) || 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-lime-500/15 border border-lime-500/25 flex items-center justify-center">
              <Wallet size={15} className="text-lime-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Cash Drawer</h2>
              <p className="text-[10px] text-slate-400">Opening float · petty cash · closing count</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleHistory}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl transition-colors ${showHistory ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <History size={12} /> Variance history
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : showHistory ? (
            /* ── Variance history ── */
            <div className="space-y-2">
              {history?.shortDays > 0 && (
                <div className="flex items-center gap-2 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5">
                  <AlertTriangle size={14} className="text-rose-500 shrink-0" />
                  <p className="text-xs text-rose-600 font-semibold">
                    Cash was short on {history.shortDays} of the last {history.totalDays} closed days.
                  </p>
                </div>
              )}
              {!history || history.sessions.length === 0 ? (
                <p className="text-center text-xs text-slate-400 py-10">No closed drawer sessions yet</p>
              ) : history.sessions.map(s => (
                <div key={s.date} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-700">{s.date}</p>
                    <p className="text-[10px] text-slate-400">
                      Float {fmt(s.openingFloat)} · sales {fmt(s.cashSalesAtClose)} · expenses −{fmt(s.cashExpensesAtClose)} · expected {fmt(s.expectedClosing)} · counted {fmt(s.actualClosing)}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-black px-2.5 py-1 rounded-lg ${s.variance === 0 ? "bg-emerald-50 text-emerald-600" : s.variance > 0 ? "bg-amber-50 text-amber-600" : "bg-rose-50 text-rose-600"}`}>
                    {s.variance === 0 ? "✓ Tally" : s.variance > 0 ? <><TrendingUp size={11} /> +{fmt(s.variance)}</> : <><TrendingDown size={11} /> −{fmt(Math.abs(s.variance))}</>}
                  </span>
                </div>
              ))}
            </div>
          ) : !drawer ? (
            /* ── Open drawer ── */
            <div className="text-center py-8">
              <Unlock size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-700 mb-1">Open today's drawer</p>
              <p className="text-xs text-slate-400 mb-4">Count the cash you're starting the day with</p>
              <div className="flex items-center justify-center gap-2">
                <input
                  type="number" min="0" placeholder="Opening float ₹"
                  value={floatInput} onChange={e => setFloatInput(e.target.value)}
                  className="w-40 border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-lime-500"
                />
                <button onClick={open} disabled={busy}
                  className="px-5 py-2.5 text-sm font-bold bg-lime-600 hover:bg-lime-700 text-white rounded-xl disabled:opacity-50">
                  Open Drawer
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Live summary ── */}
              <div className="grid grid-cols-2 sm:[grid-template-columns:repeat(auto-fit,minmax(112px,1fr))] gap-2">
                {[
                  { label: "Opening float", value: fmt(drawer.openingFloat) },
                  { label: "Cash sales", value: fmt(data.cashSales) },
                  { label: "Cash expenses", value: `−${fmt(data.cashExpenses)}`, cls: "text-rose-600" },
                  { label: "Cash in", value: `+${fmt(inSum)}`, cls: "text-emerald-600" },
                  { label: "Cash out", value: `−${fmt(outSum)}`, cls: "text-rose-600" },
                  { label: drawer.status === "CLOSED" ? "Variance" : "Expected in drawer", value: drawer.status === "CLOSED" ? fmt(drawer.variance) : fmt(data.expectedClosing), cls: drawer.status === "CLOSED" ? (drawer.variance < 0 ? "text-rose-600" : "text-emerald-600") : "text-slate-900", strong: true },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl border px-3 py-2.5 min-w-0 ${c.strong ? "border-lime-300 bg-lime-50/50" : "border-slate-200 bg-slate-50/60"}`}>
                    <p className="text-[9px] font-black uppercase tracking-wider text-slate-400 whitespace-nowrap">{c.label}</p>
                    <p className={`text-sm font-black mt-0.5 whitespace-nowrap ${c.cls || "text-slate-800"}`}>{c.value}</p>
                  </div>
                ))}
              </div>

              {drawer.status === "CLOSED" ? (
                <div className={`rounded-xl px-4 py-3 text-center border ${drawer.variance === 0 ? "bg-emerald-50 border-emerald-200" : drawer.variance > 0 ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200"}`}>
                  <Lock size={16} className="mx-auto mb-1 text-slate-400" />
                  <p className="text-xs font-bold text-slate-700">
                    Drawer closed by {drawer.closedBy} — counted {fmt(drawer.actualClosing)} vs expected {fmt(drawer.expectedClosing)}
                    {drawer.cashExpensesAtClose > 0 && <> (after −{fmt(drawer.cashExpensesAtClose)} cash expenses)</>}
                  </p>
                  <p className={`text-sm font-black mt-1 ${drawer.variance === 0 ? "text-emerald-600" : drawer.variance > 0 ? "text-amber-600" : "text-rose-600"}`}>
                    {drawer.variance === 0 ? "Perfect tally ✓" : drawer.variance > 0 ? `₹${drawer.variance} excess` : `₹${Math.abs(drawer.variance)} short`}
                  </p>
                </div>
              ) : (
                <>
                  {/* ── Add entry ── */}
                  <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Petty cash entry</p>
                    <div className="flex gap-2">
                      <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
                        <button onClick={() => setEntry(e => ({ ...e, type: "OUT" }))}
                          className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${entry.type === "OUT" ? "bg-rose-500 text-white" : "bg-white text-slate-500"}`}>
                          <Minus size={11} /> Out
                        </button>
                        <button onClick={() => setEntry(e => ({ ...e, type: "IN" }))}
                          className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${entry.type === "IN" ? "bg-emerald-500 text-white" : "bg-white text-slate-500"}`}>
                          <Plus size={11} /> In
                        </button>
                      </div>
                      <input type="number" min="0" placeholder="₹" value={entry.amount}
                        onChange={e => setEntry(en => ({ ...en, amount: e.target.value }))}
                        className="w-24 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-lime-500" />
                      <input placeholder={entry.type === "OUT" ? "Reason (milk, delivery boy, gas…)" : "Reason (change added, owner deposit…)"} value={entry.reason}
                        onChange={e => setEntry(en => ({ ...en, reason: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && addEntry()}
                        className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-lime-500" />
                      <button onClick={addEntry} disabled={busy}
                        className="px-3 py-1.5 text-xs font-bold bg-slate-800 text-white rounded-lg disabled:opacity-50 shrink-0">
                        Add
                      </button>
                    </div>
                  </div>

                  {/* ── Entries list ── */}
                  {drawer.entries.length > 0 && (
                    <div className="space-y-1">
                      {drawer.entries.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50">
                          <span className={`text-[10px] font-black w-8 ${e.type === "IN" ? "text-emerald-600" : "text-rose-600"}`}>{e.type}</span>
                          <span className="flex-1 text-xs text-slate-600 truncate">{e.reason}</span>
                          <span className="text-[10px] text-slate-400">{e.by}</span>
                          <span className={`text-xs font-bold w-20 text-right ${e.type === "IN" ? "text-emerald-600" : "text-rose-600"}`}>
                            {e.type === "IN" ? "+" : "−"}{fmt(e.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Close ── */}
                  <div className="rounded-xl border-2 border-dashed border-slate-200 p-3">
                    {!closing ? (
                      <button onClick={() => setClosing(true)}
                        className="w-full flex items-center justify-center gap-2 py-2 text-xs font-bold text-slate-600 hover:text-slate-800">
                        <Lock size={13} /> Close drawer (end of day count)
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-500 flex-1">Count all cash in the drawer and enter the total:</p>
                        <input type="number" min="0" autoFocus placeholder="Actual ₹" value={actualInput}
                          onChange={e => setActualInput(e.target.value)}
                          className="w-28 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-right font-bold focus:outline-none focus:ring-1 focus:ring-lime-500" />
                        <button onClick={close} disabled={busy}
                          className="px-3 py-1.5 text-xs font-bold bg-slate-800 text-white rounded-lg disabled:opacity-50">
                          Close & Tally
                        </button>
                        <button onClick={() => setClosing(false)} className="text-xs text-slate-400">Cancel</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Expected = float + cash sales − cash expenses + in − out. Cash sales & cash-paid Expenses entries sync in automatically.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
